import { useEffect, useRef, useState } from 'react';
import dynamic from 'next/dynamic';
import Link from 'next/link';
import { useRouter } from 'next/router';
import Meta from '../components/Meta';
import { chapters, sources, CHECKED } from '../data/granton-story';
import styles from '../styles/granton-map-tour.module.css';

const StoryMap = dynamic(() => import('../components/rail/GrantonStoryMap'), { ssr: false });
const sourceKeys = Object.keys(sources);
const motion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';

export default function GrantonTramsPage() {
  const router = useRouter();
  const [active, setActive] = useState('intro');
  const [location, setLocation] = useState('granton');
  const [guided, setGuided] = useState(true);
  const [mode, setMode] = useState('tour');
  const [requestId, setRequestId] = useState(0);
  const [mounted, setMounted] = useState(false);
  const [evidenceOpen, setEvidenceOpen] = useState(false);
  const reading = useRef('intro');
  const index = chapters.findIndex(c => c.id === active);

  useEffect(() => {
    const app = document.getElementById('app-scroll-container');
    app?.classList.add('granton-editorial-scroll');
    document.documentElement.classList.add('granton-editorial-root');
    document.body.classList.add('granton-editorial-root');
    setMounted(true);
    const restore = () => {
      const params = new URLSearchParams(window.location.search);
      const id = params.get('chapter') || window.location.hash.slice(1);
      const chapter = chapters.find(c => c.id === id);
      setActive(chapter?.id || 'intro'); reading.current = chapter?.id || 'intro';
      setLocation(chapter?.location || 'granton');
      setMode(params.get('view') === 'map' ? 'explore' : 'tour');
      setGuided(params.get('view') !== 'map' && params.get('mode') !== 'explore' && !params.has('lat'));
      const hash = window.location.hash.slice(1);
      if (hash.startsWith('source-') || hash === 'evidence' || hash === 'map-evidence') {
        setEvidenceOpen(true);
        requestAnimationFrame(() => requestAnimationFrame(() => document.getElementById(hash)?.scrollIntoView()));
      } else if (chapter && params.get('view') !== 'map') {
        requestAnimationFrame(() => requestAnimationFrame(() => document.getElementById(chapter.id)?.scrollIntoView({ block: 'center' })));
      }
    };
    restore(); window.addEventListener('popstate', restore);
    return () => {
      app?.classList.remove('granton-editorial-scroll');
      document.documentElement.classList.remove('granton-editorial-root');
      document.body.classList.remove('granton-editorial-root');
      window.removeEventListener('popstate', restore);
    };
  }, []);

  useEffect(() => {
    if (mode !== 'tour' || !mounted) return;
    let frame;
    let lastScroll = window.scrollY;
    const update = () => {
      const top = document.querySelector('[data-tour-toolbar]')?.getBoundingClientRect().bottom || 112;
      const line = top + (window.innerHeight - top) * 0.55;
      const nearest = [...document.querySelectorAll('[data-tour-step]')].reduce((best, el) => {
        const rect = el.firstElementChild.getBoundingClientRect();
        const distance = Math.abs(rect.top + rect.height / 2 - line);
        return !best || distance < best.distance ? { el, distance } : best;
      }, null);
      if (nearest) {
        const id = nearest.el.id;
        reading.current = id; setActive(id);
        setLocation(chapters.find(c => c.id === id)?.location || 'granton');
      }
      if (Math.abs(window.scrollY - lastScroll) >= 8) setGuided(true);
      lastScroll = window.scrollY;
    };
    const schedule = () => { cancelAnimationFrame(frame); frame = requestAnimationFrame(update); };
    const resize = () => { schedule(); setRequestId(n => n + 1); };
    schedule(); window.addEventListener('scroll', schedule, { passive: true }); window.addEventListener('resize', resize);
    return () => { cancelAnimationFrame(frame); window.removeEventListener('scroll', schedule); window.removeEventListener('resize', resize); };
  }, [mode, mounted]);

  const goTo = id => {
    const chapter = chapters.find(c => c.id === id);
    const target = chapter?.id || 'intro';
    setMode('tour'); setGuided(true); setActive(target); reading.current = target;
    setLocation(chapter?.location || 'granton'); setRequestId(n => n + 1);
    const url = new URL(window.location.href);
    ['lat', 'lng', 'z', 'feature', 'location', 'mode'].forEach(key => url.searchParams.delete(key));
    url.searchParams.set('chapter', target); url.searchParams.set('view', 'story'); url.hash = '';
    router.replace(`${url.pathname}${url.search}`, undefined, { shallow: true, scroll: false });
    requestAnimationFrame(() => requestAnimationFrame(() => {
      const section = document.getElementById(target);
      if (section) {
        const card = section.firstElementChild.getBoundingClientRect();
        const toolbarBottom = document.querySelector('[data-tour-toolbar]').getBoundingClientRect().bottom;
        const targetTop = window.innerWidth < 768 ? Math.max(toolbarBottom + 12, window.innerHeight - card.height - 32) : toolbarBottom + (window.innerHeight - toolbarBottom - card.height) / 2;
        window.scrollTo({ top: window.scrollY + card.top - targetTop, behavior: motion() });
      }
      section?.querySelector('h1,h2')?.focus({ preventScroll: true });
    }));
  };
  const exploreMap = () => {
    setMode('explore'); setGuided(false);
    const url = new URL(window.location.href);
    url.hash = ''; url.searchParams.set('view', 'map'); url.searchParams.set('mode', 'explore'); url.searchParams.set('chapter', reading.current);
    router.replace(`${url.pathname}${url.search}`, undefined, { shallow: true, scroll: false });
    window.scrollTo({ top: 0, behavior: 'auto' });
  };
  const openSource = key => {
    setEvidenceOpen(true);
    const id = key ? `source-${key}` : 'evidence';
    const url = new URL(window.location.href); url.hash = id;
    window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
    requestAnimationFrame(() => requestAnimationFrame(() => {
      const target = document.getElementById(id);
      target?.scrollIntoView({ behavior: motion(), block: 'start' }); target?.focus({ preventScroll: true });
    }));
  };
  const references = refs => <span className={styles.references}>{refs.map(key => <a key={key} href={`#source-${key}`} onClick={e => { e.preventDefault(); openSource(key); }} aria-label={`Source: ${sources[key].title}`}>[{sourceKeys.indexOf(key) + 1}]</a>)}</span>;
  const next = () => goTo(chapters[Math.min(index + 1, chapters.length - 1)].id);

  return <div className={`planning-register ${styles.page}`}>
    <Meta title="Granton tram & development map | CityScope" description="Explore Granton’s development sites and tram route options. A short map tour by Harry Williams, with planning data, costs and accounts from Leith businesses." canonical="/granton-trams" ogType="article" ogImage="/edinburgh-tram-x-card.png" publishedTime={CHECKED} modifiedTime="2026-09-09" section="CityScope maps" structuredData={{ '@context': 'https://schema.org', '@type': 'Article', headline: 'Granton: homes, trams and route choices', datePublished: CHECKED, dateModified: '2026-09-09', author: { '@type': 'Person', name: 'Harry Williams' }, publisher: { '@type': 'Organization', name: 'CityScope' } }} />
    <a className={styles.skipLink} href="#story">Skip to the map tour</a>
    <nav className={styles.toolbar} data-tour-toolbar aria-label="Map and tour controls">
      <span className={styles.brand}>Granton <span>/ tram & development map</span></span>
      <div className={styles.modeButtons}><button type="button" aria-pressed={mode === 'tour' && guided} onClick={() => goTo(reading.current)}>{!guided ? 'Resume tour' : 'Map tour'}</button><button type="button" aria-pressed={mode === 'explore'} onClick={exploreMap}>Explore map</button></div>
      <button type="button" className={styles.evidenceButton} onClick={() => openSource()}>Sources</button>
      <span className={styles.progress}>{index < 0 ? 'Start' : `${index + 1} / ${chapters.length}`}</span>
    </nav>
    <div className={styles.stage} data-mode={mode} data-guided={guided}>
      <aside id="story-map" className={styles.mapFrame} aria-label="Interactive Granton development and tram map">
        {mounted ? <StoryMap location={location} chapter={active} guided={guided} visible requestId={requestId} onExplore={() => setGuided(false)} onReturn={() => goTo(reading.current)} onFeatureChapter={goTo} onEvidence={() => openSource()} onShareUrl={url => router.replace(url, undefined, { shallow: true, scroll: false })} mapTour tourReading={mode === 'tour' && guided} /> : <p className={styles.loading}>Loading the map…</p>}
      </aside>
      <main id="story" className={styles.scrollTrack} aria-label="Granton map tour">
        <header id="intro" data-tour-step className={styles.step} data-active={active === 'intro'}><div className={`${styles.card} ${styles.introCard}`}>
          <span className={styles.eyebrow}>CityScope / Map tour</span><h1 tabIndex={-1}>Granton, on the map.</h1><p className={styles.byline}>By Harry Williams</p>
          <p>Development sites, tram routes and a few reasons I support the connection. Tap a pin to see the project, or scroll for the tour.</p>
          <div className={styles.introData}><span><b>17</b> places</span><span><b>2</b> northern routes</span></div>
          <button type="button" className={styles.nextButton} onClick={next}>Scroll to start <span aria-hidden="true">↓</span></button>
        </div></header>
        {chapters.map((chapter, i) => <section key={chapter.id} id={chapter.id} data-tour-step className={styles.step} data-active={active === chapter.id}><div className={styles.card} style={{ '--card-accent': chapter.accent || '#805b17' }}>
          <div className={styles.cardMeta}><span>{String(i + 1).padStart(2, '0')} / {chapter.kind}</span><span>{chapter.short}</span></div>
          <h2 tabIndex={-1}>{chapter.title}</h2>
          {chapter.stats && <dl className={styles.stats}>{chapter.stats.map(([number, label]) => <div key={label}><dt>{label}</dt><dd>{number}</dd></div>)}</dl>}
          {chapter.metric && <div className={styles.metric}><strong>{chapter.metric}</strong><span>{chapter.metricLabel}</span></div>}
          {chapter.quotes?.map(quote => <figure className={styles.quote} key={quote.business}><blockquote>“{quote.text}”</blockquote><figcaption><b>{quote.business}</b> · {quote.speaker}{references(quote.refs)}</figcaption></figure>)}
          <p>{chapter.text}{references(chapter.refs)}</p>
          {chapter.id === 'neighbourhood' && <button type="button" className={styles.textButton} onClick={exploreMap}>Explore the 17 development pins ↗</button>}
          {chapter.id === 'home' ? <Link className={styles.textButton} href="/planning-updates/edi?q=Granton">Granton planning updates ↗</Link> : <button type="button" className={styles.nextButton} onClick={next} aria-label={`Continue after ${chapter.short}`}>Keep scrolling <span aria-hidden="true">↓</span></button>}
        </div></section>)}
      </main>
    </div>
    <footer className={styles.footer}>
      <details id="evidence" open={evidenceOpen} onToggle={e => setEvidenceOpen(e.currentTarget.open)} className={styles.evidence}>
        <summary>Sources & map data <span>{sourceKeys.length} references</span></summary>
        <p>Sources checked 8 September 2026. Project images have their own credits in the map cards.</p>
        <div id="map-evidence" tabIndex={-1} className={styles.mapEvidence}><h2>About the map</h2><p>Solid red lines are the operating tram. Dashed and dotted lines are the proposed routes. Stops and development areas are indicative. Development boundaries use CityScope’s February 2026 snapshot; the cards link to their records and later project sources. Missing route geometry has not been filled in. The Granton–Newhaven loop is not included in the current proposal shown.</p><p>Map: © <a href="https://www.openstreetmap.org/copyright">OpenStreetMap contributors</a>. Routes: council consultation material. Development records: CityScope.</p></div>
        <ol className={styles.sourceList}>{sourceKeys.map(key => <li key={key} id={`source-${key}`} tabIndex={-1}><a href={sources[key].url} target="_blank" rel="noreferrer">{sources[key].title} ↗</a><p>{sources[key].publisher} · {sources[key].date || 'undated'}<br />{sources[key].locator}</p></li>)}</ol>
      </details>
      <div className={styles.footerLinks}><Link href="/">CityScope</Link><Link href="/edi-tram-plan">Full Edinburgh tram explorer ↗</Link><button type="button" onClick={() => goTo('intro')}>Back to the map ↑</button></div>
    </footer>
    <style jsx global>{`html.granton-editorial-root, body.granton-editorial-root { overflow: visible; overflow-x: clip; } #app-scroll-container.granton-editorial-scroll { overflow: visible; } #app-scroll-container.granton-editorial-scroll::before, #app-scroll-container.granton-editorial-scroll::after { display: none; }`}</style>
  </div>;
}
